import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { SearchableCombobox } from "@/components/ui/searchable-combobox"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { catalogModelComboboxOption } from "@/lib/generation-model-option"
import { patchShotVideoModelKey } from "@/lib/inertia-actions"
import {
  effectiveShotVideoModelKey,
  findShotVideoModel,
  videoResolutionAfterModelChange,
} from "@/lib/shot-video-settings"

export type ShotVideoModelSaved = {
  videoModelKey: string | null
  videoResolution: string | null
}

export type ShotVideoModelChipProps = {
  projectId: number
  shotId: number
  /** Shot override; null/undefined inherits project. */
  shotVideoModelKey?: string | null
  /** Current shot resolution override; cleared locally when invalid for the new model. */
  shotVideoResolution?: string | null
  onSaved?: (next: ShotVideoModelSaved) => void
}

export function ShotVideoModelChip({
  projectId,
  shotId,
  shotVideoModelKey,
  shotVideoResolution,
  onSaved,
}: ShotVideoModelChipProps) {
  const { t } = useTranslation(["shot", "common", "workspace"])
  const { project, project_permissions, generation_models_by_context } = useShotPanelWorkspace()
  const canEdit = Boolean(project_permissions?.can_edit_content)
  const models = useMemo(
    () => generation_models_by_context?.shot_video ?? [],
    [generation_models_by_context?.shot_video],
  )
  const projectKey = project?.effective_video_model?.toString().trim() || ""
  const hasOverride = Boolean(shotVideoModelKey?.toString().trim())
  const currentKey = effectiveShotVideoModelKey(shotVideoModelKey, project)
  const currentModel = findShotVideoModel(currentKey, models)
  const shortLabel = currentModel?.label?.trim() || currentKey || "—"

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(hasOverride ? currentKey : "")

  useEffect(() => {
    if (open) setDraft(hasOverride ? effectiveShotVideoModelKey(shotVideoModelKey, project) : "")
  }, [open, hasOverride, shotVideoModelKey, project])

  const options = useMemo(() => {
    const sorted = [...models].sort((a, b) => {
      const aEnabled = a.enabled !== false
      const bEnabled = b.enabled !== false
      if (aEnabled !== bEnabled) return aEnabled ? -1 : 1
      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    })
    const platformDisabledLabel = t("common:platformDisabledModel")
    return [
      {
        value: "",
        label: t("workspace:providers.defaultFromStudio"),
      },
      ...sorted.map((model) => catalogModelComboboxOption(model, platformDisabledLabel)),
    ]
  }, [models, t])

  const clearsOverride = draft === "" || draft === projectKey
  const noChange = clearsOverride ? !hasOverride : shotVideoModelKey === draft

  const handleSave = () => {
    if (noChange) {
      setOpen(false)
      return
    }
    const nextOverride = clearsOverride ? null : draft
    const prevOverride = hasOverride ? (shotVideoModelKey ?? null) : null
    const prevResolution = shotVideoResolution?.toString().trim() || null
    // Same as server: blank shot model → validate against project effective model.
    const nextModelKey = nextOverride ?? projectKey
    const nextResolution = videoResolutionAfterModelChange(shotVideoResolution, nextModelKey, models)
    onSaved?.({ videoModelKey: nextOverride, videoResolution: nextResolution })
    setOpen(false)
    void patchShotVideoModelKey(projectId, shotId, nextOverride)
      .then(() => undefined)
      .catch((err: Error) => {
        onSaved?.({ videoModelKey: prevOverride, videoResolution: prevResolution })
        toast.error(err.message || t("common.saveErrorRetry"))
      })
  }

  const chip = <span className="max-w-[9rem] truncate tabular-nums">{shortLabel}</span>

  return (
    <>
      {canEdit ? (
        <ActionHintTooltip label={t("panel.changeVideoModel")}>
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md text-xs font-normal text-ui-text-secondary hover:text-ui-text-primary focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
            aria-label={shortLabel}
            onClick={() => setOpen(true)}
          >
            {chip}
          </button>
        </ActionHintTooltip>
      ) : (
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs font-normal text-ui-text-secondary"
          aria-label={shortLabel}
          title={shortLabel}
        >
          {chip}
        </span>
      )}

      {canEdit ? (
        <WorkspaceDialog
          open={open}
          onOpenChange={setOpen}
          title={t("panel.videoModel")}
          description={t("panel.videoModelHint")}
          closeAriaLabel={t("common:close")}
          size="sm"
          nested
          bodyClassName="p-5 pt-4"
          footer={
            <>
              <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="button" size="sm" onClick={handleSave}>
                {t("common:save")}
              </Button>
            </>
          }
        >
          <SearchableCombobox
            value={draft}
            onChange={setDraft}
            options={options}
            placeholder={shortLabel}
            portalToBody
          />
        </WorkspaceDialog>
      ) : null}
    </>
  )
}
